import { absolutePosition } from "./auto-fuse";
import { getDefinition } from "./catalog";
import { effectiveDimensions } from "./effective";
import type { ComponentNode, System, SystemNode, ZoneNode } from "./types";

/** `position` is in the NEW parent's space; apply the pair together or the node jumps. */
export type ZoneReparent = {
  id: string;
  parentId: string | null;
  position: { x: number; y: number };
};

/** Junctions render as a small dot, not at their (unused) catalog dimensions. */
const JUNCTION_SIZE = 14;

function componentSize(node: ComponentNode): { width: number; height: number } | null {
  const def = getDefinition(node.definitionId, node.customDefinition);
  if (!def) return null;
  if (def.renderVariant === "junction") {
    return { width: JUNCTION_SIZE, height: JUNCTION_SIZE };
  }
  return effectiveDimensions(def, node);
}

/**
 * Nodes whose parent does not change are left out, so applying the plan never re-bases one
 * twice. Zones are skipped: React Flow does not nest them, so a nested zone would be
 * re-based against a `parentId` the renderer ignores — a guaranteed jump.
 */
export function planZoneReparent(
  system: System,
  movedIds: Iterable<string>,
): ZoneReparent[] {
  const zones: { node: ZoneNode; x: number; y: number }[] = [];
  for (const n of system.nodes) {
    if (n.kind !== "zone") continue;
    const abs = absolutePosition(n, system.nodes);
    zones.push({ node: n, x: abs.x, y: abs.y });
  }

  const byId = new Map<string, SystemNode>(system.nodes.map((n) => [n.id, n]));
  const changes: ZoneReparent[] = [];

  for (const id of movedIds) {
    const node = byId.get(id);
    if (!node || node.kind === "zone") continue;
    const size = componentSize(node);
    if (!size) continue;

    const abs = absolutePosition(node, system.nodes);
    const cx = abs.x + size.width / 2;
    const cy = abs.y + size.height / 2;

    const containing = zones.find(
      (z) =>
        cx >= z.x &&
        cx <= z.x + z.node.width &&
        cy >= z.y &&
        cy <= z.y + z.node.height,
    );

    const parentId = containing?.node.id ?? null;
    if (parentId === (node.parentId ?? null)) continue;

    changes.push({
      id,
      parentId,
      position: {
        x: abs.x - (containing?.x ?? 0),
        y: abs.y - (containing?.y ?? 0),
      },
    });
  }

  return changes;
}
